import { useCallback, useEffect, useRef, useState } from "react";
import './ProductSliderMultiSpecific.scss';
import hearImg from '../assets/heart.svg';
import { UseInterval } from "../UseInterval";

// slideXPos steps: 0, -100, -200, … (-100 per panel). Legacy track math assumed 8 panels; mobile scroll uses 7 panels (7 images).
function slideTransformFromX(slideXPos) {
    return `translateX(${slideXPos * 0.125}%)`;
}

/** Leading clone of last image + 7 real + trailing clone of first — infinite scroll without hard stops at edges. */
const MULTI_SLOT_ORDER = [6, 0, 1, 2, 3, 4, 5, 6, 0];
const MULTI_FIRST_REAL = 1;
const MULTI_LAST_REAL = 7;
const MULTI_LAST_CLONE = 8;

/** Mobile: dots + scroll-sync; jumps at clone slides so scroll is never “stuck” at the visual ends. */
function ProductSliderMultiStrip({
    images,
    stripKey,
    stripIndex,
    onFirstHeroImageSettled,
    suppressImagePlaceholders,
    swichSlides,
}) {
    const mainMultiRef = useRef(null);
    const [activeDot, setActiveDot] = useState(0);
    const jumpingRef = useRef(false);
    const initializingRef = useRef(true);

    const getPhysicalIndex = (el) => {
        const w = el.clientWidth;
        if (w <= 0) return 0;
        return Math.round(el.scrollLeft / w);
    };

    /** Dots only — runs while user scrolls; does not teleport (avoids fighting scroll-snap). */
    const syncDotsFromScroll = useCallback(() => {
        const el = mainMultiRef.current;
        if (!el || jumpingRef.current) return;
        const w = el.clientWidth;
        if (w <= 0) return;
        const idx = getPhysicalIndex(el);
        if (idx >= MULTI_FIRST_REAL && idx <= MULTI_LAST_REAL) {
            setActiveDot(idx - 1);
        } else if (idx === 0) {
            setActiveDot(6);
        } else if (idx === MULTI_LAST_CLONE) {
            setActiveDot(0);
        }
    }, []);

    /** Instant jump with snap disabled — run only after scroll/gesture has settled (scrollend or debounce). */
    const teleportTo = useCallback((el, leftPx) => {
        jumpingRef.current = true;
        el.classList.add('main-multi--teleport');
        el.scrollLeft = leftPx;
        requestAnimationFrame(() => {
            requestAnimationFrame(() => {
                el.classList.remove('main-multi--teleport');
                jumpingRef.current = false;
            });
        });
    }, []);

    /** Clone slides → real slides; must not run on every scroll tick (that caused snap vs JS “rattle”). */
    const applyInfiniteWrap = useCallback(() => {
        const el = mainMultiRef.current;
        if (!el || jumpingRef.current) return;
        const w = el.clientWidth;
        if (w <= 0) return;
        const idx = getPhysicalIndex(el);

        if (idx === 0) {
            if (initializingRef.current) {
                teleportTo(el, MULTI_FIRST_REAL * w);
                initializingRef.current = false;
                setActiveDot(0);
                return;
            }
            teleportTo(el, MULTI_LAST_REAL * w);
            setActiveDot(6);
            return;
        }
        if (idx === MULTI_LAST_CLONE) {
            teleportTo(el, MULTI_FIRST_REAL * w);
            setActiveDot(0);
        }
    }, [teleportTo]);

    useEffect(() => {
        const el = mainMultiRef.current;
        if (!el) return;
        initializingRef.current = true;
        let rafId = 0;
        const apply = () => {
            const w = el.clientWidth;
            if (w <= 0) {
                rafId = requestAnimationFrame(apply);
                return;
            }
            el.classList.add('main-multi--teleport');
            jumpingRef.current = true;
            el.scrollLeft = MULTI_FIRST_REAL * w;
            requestAnimationFrame(() => {
                requestAnimationFrame(() => {
                    el.classList.remove('main-multi--teleport');
                    jumpingRef.current = false;
                    initializingRef.current = false;
                });
            });
            setActiveDot(0);
        };
        apply();
        return () => cancelAnimationFrame(rafId);
    }, [stripKey]);

    useEffect(() => {
        const el = mainMultiRef.current;
        if (!el) return;
        let idleTimer = null;

        const onScroll = () => {
            syncDotsFromScroll();
            clearTimeout(idleTimer);
            idleTimer = window.setTimeout(() => {
                applyInfiniteWrap();
            }, 150);
        };

        const onScrollEnd = () => {
            clearTimeout(idleTimer);
            applyInfiniteWrap();
        };

        const ro = new ResizeObserver(() => syncDotsFromScroll());
        ro.observe(el);
        el.addEventListener('scroll', onScroll, { passive: true });
        el.addEventListener('scrollend', onScrollEnd);
        syncDotsFromScroll();
        return () => {
            ro.disconnect();
            clearTimeout(idleTimer);
            el.removeEventListener('scroll', onScroll);
            el.removeEventListener('scrollend', onScrollEnd);
        };
    }, [applyInfiniteWrap, syncDotsFromScroll, stripKey]);

    const scrollToPanel = (dotIndex) => {
        const el = mainMultiRef.current;
        if (!el) return;
        const w = el.clientWidth;
        if (w <= 0) return;
        el.scrollTo({ left: (MULTI_FIRST_REAL + dotIndex) * w, behavior: 'smooth' });
    };

    return (
        <>
            <div className="main-multi" ref={mainMultiRef}>
                <div className="multi-slide" style={swichSlides}>
                    {MULTI_SLOT_ORDER.map((imgIdx, slotIdx) => (
                        <div key={`slide-${stripIndex}-slot-${slotIdx}`}>
                            <SlideProductImage
                                src={images[imgIdx]}
                                suppressLoader={suppressImagePlaceholders}
                                onHeroSettled={
                                    stripIndex === 0 && slotIdx === MULTI_FIRST_REAL
                                        ? onFirstHeroImageSettled
                                        : undefined
                                }
                            />
                        </div>
                    ))}
                </div>
            </div>

            {/* Logical 7 segments — same index as dots (native bar hid: it counted clone panels too). */}
            <div className="multi-scroll-rail" aria-hidden>
                <div className="multi-scroll-rail__track">
                    <div
                        className="multi-scroll-rail__thumb"
                        style={{ left: `${(activeDot * 100) / 7}%` }}
                    />
                </div>
            </div>

            <ul className="slick-smalldots" aria-label="Product images">
                {[0, 1, 2, 3, 4, 5, 6].map((i) => (
                    <li key={i} className={activeDot === i ? 'slick-active' : ''}>
                        <button type="button" onClick={() => scrollToPanel(i)} aria-label={`Image ${i + 1}`} aria-current={activeDot === i ? 'true' : undefined} />
                    </li>
                ))}
            </ul>
        </>
    );
}

function SlideProductImage({ src, onHeroSettled, suppressLoader = false }) {
    const [loaded, setLoaded] = useState(false);

    const settleHero = useCallback(() => {
        setLoaded(true);
        if (typeof onHeroSettled === "function") onHeroSettled();
    }, [onHeroSettled]);

    return (
        <div className="slide-img-cell">
            <div
                className={"slide-img-ph" + (loaded ? " slide-img-ph--out" : "")}
                aria-hidden
            >
                {!suppressLoader && <span className="slide-img-ph__loader" />}
            </div>
            <img
                src={src}
                alt=""
                decoding="async"
                className={"slide-img" + (loaded ? " slide-img--visible" : "")}
                onLoad={settleHero}
                onError={settleHero}
            />
        </div>
    );
}

export function ProductSliderMultiSpecific({ slides, product, onFirstImageLoad, suppressImagePlaceholders = false }) {
    const rawTitle = product?.title ?? 'Product';
    const color = Array.isArray(product?.color) ? product.color[0] : product?.color;
    const title = color ? `${rawTitle} (${color})` : rawTitle;
    const price = product?.price;
    const brand = (Array.isArray(product?.brand) ? product.brand[0] : product?.brand) ?? '—';
    const mrp = product?.mrp;
    const discount = (mrp != null && price != null && mrp > 0) ? Math.round((1 - price / mrp) * 100) : 0;

    const [slideXPos, setSlideXPos] = useState(0);
    const [swichSlides, setSwichSlides] = useState({ transform: slideTransformFromX(0), transition: 'all 0s linear' });
    // const [isHover, setIsHover] = useState(false)
    const [isHover, setIsHover] = useState(-1)
    const heroLoadNotified = useRef(false);

    const handleFirstHeroImageSettled = useCallback(() => {
        if (heroLoadNotified.current) return;
        heroLoadNotified.current = true;
        if (typeof onFirstImageLoad === "function") onFirstImageLoad();
    }, [onFirstImageLoad]);

    const heroSlideKey = slides?.[0]?.[0] ?? "";

    useEffect(() => {
        heroLoadNotified.current = false;
    }, [heroSlideKey]);

    let [delay, setDelay] = useState(null);

    useEffect(() => {
        if (swichSlides.transition === '' && delay === null) {
            console.log('in');
            setSwichSlides({ transform: slideTransformFromX(0), transition: 'none' });
            setSlideXPos(0)
        }
        else if (swichSlides.transition === '') {
            console.log('in');
            setSwichSlides({ transform: slideTransformFromX(0), transition: 'none' });
            setDelay(10);
            setSlideXPos(0)
        }
    }, [swichSlides])

    useEffect(() => {
        console.log('delay -- ', delay);
    }, [delay])


    function swichSlideXPos() {
        console.log('slideXPos - ', slideXPos);

        if (slideXPos === -700) {
            console.log('triggere 1');
            setSwichSlides({ transform: slideTransformFromX(0), transition: '' })
            setSlideXPos(0);
        }
        else {
            console.log('triggere 3');
            console.log(slideXPos - 600);
            setSwichSlides({ transform: slideTransformFromX(slideXPos - 100), transition: 'all 1s linear' });
            setSlideXPos(slideXPos - 100);
        }
    }


    // UseInterval(() => {
    //     if (delay === 500) {
    //         setDelay(1500);
    //     }
    //     else if (delay === 10) {
    //         setDelay(1500);
    //     }
    //     swichSlideXPos();
    // }, delay);

    function unsetHover() {
        console.log('in unsetHover');
        // setDelay(null);
        setSwichSlides({ transform: slideTransformFromX(0), transition: '' })
        setIsHover(-1);
    }

    let slideInterval = null;


    return (
        <div className="product-slider-multi-specific-mob">
            {
                slides.map((images, index) => {
                    return (
                        <div className="slide-main">
                            {/* <div className='slide-container' style={swichSlides} onMouseOver={() => setIsHover(true)}
                    onMouseLeave={() => setIsHover(false)}> */}
                            {/* <div className='slide-container' style={swichSlides}> */}
                            <div className='slide-container'>
                                {
                                        <div className="multi-container">
                                            {/* <div className="main-multi" onMouseEnter={() => handleDelayChange()} onMouseLeave={() => unsetHover()} style={isHover === true ? { display: 'block' } : { display: 'none', transition: 'none' }}> */}

                                                <>
                                                    <ProductSliderMultiStrip
                                                        images={images}
                                                        stripKey={images?.[0] ?? String(index)}
                                                        stripIndex={index}
                                                        onFirstHeroImageSettled={handleFirstHeroImageSettled}
                                                        suppressImagePlaceholders={suppressImagePlaceholders}
                                                        swichSlides={swichSlides}
                                                    />
                                                    <div className="multi-meta-data">
                                                        {/* <div className="product-actions">
                                                            <span className="product-option-container product-actionsButton product-option">
                                                                <span className="myntraweb-sprite product-notWishlistedIcon sprites-notWishlisted">
                                                                    <img src={hearImg} />
                                                                </span>
                                                                <span>wishlist</span>
                                                            </span>
                                                        </div> */}
                                                        {/* <div className="title multi-image-readjust">Sizes: S</div> */}
                                                        {/* <div className="wishlist">
                                                                <img src={hearImg} />
                                                            </div> */}
                                                        <div className="brand">{brand}</div>
                                                        <div className="title">{title}</div>
                                                        <div className="offer-details multi-image-readjust">
                                                            {price != null && <span>Rs. {price}</span>}
                                                            {mrp != null && mrp > 0 && <span>Rs. {mrp}</span>}
                                                            {discount > 0 && <span>({discount}% OFF)</span>}
                                                        </div>
                                                    </div>
                                                </>


                                        </div>

                                }
                            </div>

                        </div>
                    )
                })
            }
            {/* <div className="mybutton">
                <button onClick={swichSlideXPos}>Click Me</button>
            </div> */}
            {/* {isHover} */}
        </div>
    )
}





// import { useEffect, useState } from "react";
// import './ProductSliderMulti.scss';
// import img1 from '../assets/image-1.jpg'
// import img2 from '../assets/image-2.jpg'
// import img3 from '../assets/image-3.jpg'
// import img4 from '../assets/image-4.jpg'
// import img5 from '../assets/image-5.jpg'
// import hearImg from '../assets/heart.svg';
// import { UseInterval } from "../UseInterval";

// export function ProductSliderMulti({ slides }) {

//     const [slideXPos, setSlideXPos] = useState(0);
//     const [swichSlides, setSwichSlides] = useState({ '-webkit-transform': `translateX(${0}px)`, transition: 'all 0s linear' });
//     // const [isHover, setIsHover] = useState(false)
//     const [isHover, setIsHover] = useState(-1)
//     const [activeSmalldots, setActiveSmalldots] = useState(0)

//     let [delay, setDelay] = useState(null);

//     useEffect(() => {
//         if (swichSlides.transition === '' && delay === null) {
//             console.log('in');
//             setSwichSlides({ '-webkit-transform': `translateX(${0}px)`, transition: 'none' });
//             setSlideXPos(0)
//         }
//         else if (swichSlides.transition === '') {
//             console.log('in');
//             setSwichSlides({ '-webkit-transform': `translateX(${0}px)`, transition: 'none' });
//             setDelay(10);
//             setSlideXPos(0)
//         }
//     }, [swichSlides])

//     useEffect(() => {
//         console.log('delay -- ', delay);
//     }, [delay])


//     function swichSlideXPos() {
//         console.log('slideXPos - ', slideXPos);

//         if (slideXPos === -1260) {
//             console.log('triggere 1');
//             setSwichSlides({ '-webkit-transform': `translateX(${0}px)`, transition: '' })
//             setSlideXPos(0);
//         }
//         else {
//             console.log('triggere 3');
//             console.log(slideXPos - 180);
//             setSwichSlides({ '-webkit-transform': `translateX(${slideXPos - 180}px)`, transition: 'all 1s linear' });
//             setSlideXPos(slideXPos - 180);
//             if (slideXPos === -1080) {
//                 setActiveSmalldots(0);
//             }
//             else {
//                 setActiveSmalldots(activeSmalldots + 1);
//             }
//         }
//     }

//     // let [count, setCount] = useState(0);

//     UseInterval(() => {
//         // Your custom logic here
//         // setCount(count + 1);
//         if (delay === 500) {
//             setDelay(1500);
//         }
//         else if (delay === 10) {
//             setDelay(1500);
//         }
//         swichSlideXPos();
//     }, delay);

//     function handleDelayChange() {
//         console.log('in handleDelayChange');
//         // setIsHover(index)
//         setDelay(500);
//     }

//     function unsetHover() {
//         console.log('in unsetHover');
//         setDelay(null);
//         setSwichSlides({ '-webkit-transform': `translateX(${0}px)`, transition: '' })
//         setIsHover(-1);
//         setActiveSmalldots(0);
//     }

//     let slideInterval = null;

//     const [clickedd, setClickedd] = useState(false)


//     return (
//         <div className="product-slider-multi-jo">
//             {
//                 slides.map((images, index) => {
//                     return (
//                                     isHover === index ? (
//                                                 <>
//                                                     <div className="main-multi" onClick={() => setClickedd(!clickedd)}>
//                                                         {console.log('called multi')}

//                                                         {/* <div className="multi-slide" onMouseOver={() => handleDelayChange()} style={swichSlides}> */}
//                                                         {/* <div className="multi-slide" style={swichSlides}> */}
//                                                         <div className={clickedd ? "multi-slide-2" : "multi-slide"}>
//                                                             {/* <div>
//                                                                 <img src={images[0]} />
//                                                             </div>
//                                                             <div>
//                                                                 <img src={images[1]} />
//                                                             </div>
//                                                             <div>
//                                                                 <img src={images[2]} />
//                                                             </div>
//                                                             <div>
//                                                                 <img src={images[3]} />
//                                                             </div>
//                                                             <div>
//                                                                 <img src={images[4]} />
//                                                             </div>
//                                                             <div>
//                                                                 <img src={images[5]} />
//                                                             </div>
//                                                             <div>
//                                                                 <img src={images[6]} />
//                                                             </div>
//                                                             <div>
//                                                                 <img src={images[0]} />
//                                                             </div> */}

//                                                             <div></div>
//                                                             <div></div>
//                                                             <div></div>
//                                                         </div>

//                                                         <ul className="slick-smalldots">
//                                                             {/* <li className={activeSmalldots === 0 ? 'slick-active' : ''}>
//                                                                 <button>1</button>
//                                                             </li>
//                                                             <li className={activeSmalldots === 1 ? 'slick-active' : ''}>
//                                                                 <button>2</button>
//                                                             </li>
//                                                             <li className={activeSmalldots === 2 ? 'slick-active' : ''}>
//                                                                 <button>3</button>
//                                                             </li>
//                                                             <li className={activeSmalldots === 3 ? 'slick-active' : ''}>
//                                                                 <button>4</button>
//                                                             </li>
//                                                             <li className={activeSmalldots === 4 ? 'slick-active' : ''}>
//                                                                 <button>5</button>
//                                                             </li>
//                                                             <li className={activeSmalldots === 5 ? 'slick-active' : ''}>
//                                                                 <button>6</button>
//                                                             </li>
//                                                             <li className={activeSmalldots === 6 ? 'slick-active' : ''}>
//                                                                 <button>7</button>
//                                                             </li> */}
//                                                         </ul>
//                                                     </div>

//                                                     {/* <div className="multi-meta-data">
//                                                         <div className="product-actions">
//                                                             <span className="product-option-container product-actionsButton product-option">
//                                                                 <span className="myntraweb-sprite product-notWishlistedIcon sprites-notWishlisted">
//                                                                     <img src={hearImg} />
//                                                                 </span>
//                                                                 <span>wishlist</span>
//                                                             </span>
//                                                         </div>
//                                                         <div className="title multi-image-readjust">Sizes: S</div>
//                                                         <div className="offer-details multi-image-readjust">
//                                                             <span>Rs. 258</span>
//                                                             <span>Rs. 699</span>
//                                                             <span>(63% OFF)</span>
//                                                         </div>
//                                                     </div> */}
//                                                 </>

//                                     )
//                                         :
//                                         (
//                                             <div className="main-single" onMouseEnter={() => setIsHover(index)} style={isHover !== index  ? { display: 'block' }
//                                                 : { display: 'none', transition: 'none' }} onMouseLeave={() => setIsHover(index)}>
//                                                 {console.log('called single')}

//                                                 {/* {!isHover && */}
//                                                     <>
//                                                         <div className="ratings-container">
//                                                             <span>4.2</span>
//                                                             <span>*</span>
//                                                             <div className="product-ratingsCount">
//                                                                 <div className="product-seperator">|</div>
//                                                                 4.2K
//                                                             </div>
//                                                         </div>
//                                                         <div className="single-slide">
//                                                             {/* <img src={images[0]} /> */}
//                                                         </div>

//                                                         <div className="meta-data">
//                                                             <div className="brand">Roadster</div>
//                                                             <div className="title">Oversized Cotton T-shirt</div>
//                                                             <div className="offer-details">
//                                                                 <span>Rs. 258</span>
//                                                                 <span>Rs. 699</span>
//                                                                 <span>(63% OFF)</span>
//                                                             </div>
//                                                         </div>
//                                                     </>
//                                                 {/* } */}

//                                             </div>
//                                         )
                                

                          
//                     )
//                 })
//             }
//             {/* <div className="mybutton">
//                 <button onClick={swichSlideXPos}>Click Me</button>
//             </div> */}
//             {/* {isHover} */}
//         </div>
//     )
// }